import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx } from './ui';

export type ToastLevel = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  title?: string;
  message: string;
  level?: ToastLevel;
  actions?: ToastAction[];
  /** ms; 0 keeps the toast until dismissed */
  timeoutMs?: number;
  /** Replaces an existing toast with the same key instead of stacking */
  key?: string;
}

interface Toast extends Required<Omit<ToastInput, 'key' | 'actions' | 'title'>> {
  id: number;
  key?: string;
  title?: string;
  actions: ToastAction[];
}

export interface ToastApi {
  push: (t: ToastInput) => number;
  dismiss: (id: number) => void;
  info: (message: string, title?: string) => number;
  success: (message: string, title?: string) => number;
  warning: (message: string, title?: string) => number;
  error: (message: string, title?: string) => number;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('ToastProvider missing');
  return api;
}

const LEVEL_STYLE: Record<ToastLevel, string> = {
  info: 'border-blue-200 bg-white',
  success: 'border-green-200 bg-white',
  warning: 'border-amber-300 bg-white',
  error: 'border-red-300 bg-white',
};

const LEVEL_BAR: Record<ToastLevel, string> = {
  info: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setToasts((list) => list.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = ++seq.current;
      const toast: Toast = {
        id,
        key: input.key,
        title: input.title,
        message: input.message,
        level: input.level ?? 'info',
        actions: input.actions ?? [],
        timeoutMs: input.timeoutMs ?? (input.actions?.length ? 15_000 : 5_000),
      };
      setToasts((list) => {
        const kept = input.key ? list.filter((x) => x.key !== input.key) : list;
        return [...kept.slice(-4), toast];
      });
      if (toast.timeoutMs > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), toast.timeoutMs),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      info: (message, title) => push({ message, title, level: 'info' }),
      success: (message, title) => push({ message, title, level: 'success' }),
      warning: (message, title) => push({ message, title, level: 'warning' }),
      error: (message, title) => push({ message, title, level: 'error', timeoutMs: 8_000 }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cx(
              'pointer-events-auto relative overflow-hidden rounded-xl border shadow-lg',
              LEVEL_STYLE[t.level],
            )}
          >
            <span className={cx('absolute inset-y-0 left-0 w-1', LEVEL_BAR[t.level])} />
            <div className="pl-4 pr-3 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {t.title && <p className="text-sm font-semibold text-ink">{t.title}</p>}
                  <p className="text-sm text-ink break-words">{t.message}</p>
                </div>
                <button
                  type="button"
                  aria-label="닫기"
                  onClick={() => dismiss(t.id)}
                  className="-mr-1 -mt-1 rounded p-1 text-ink-muted hover:bg-black/5"
                >
                  <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" aria-hidden>
                    <path d="M6.3 5.3a1 1 0 0 0-1.4 1.4L8.6 10l-3.7 3.3a1 1 0 1 0 1.4 1.4L10 11.4l3.3 3.3a1 1 0 0 0 1.4-1.4L11.4 10l3.3-3.3a1 1 0 0 0-1.4-1.4L10 8.6 6.3 5.3z" />
                  </svg>
                </button>
              </div>
              {t.actions.length > 0 && (
                <div className="mt-2 flex gap-2">
                  {t.actions.map((a) => (
                    <button
                      key={a.label}
                      type="button"
                      onClick={() => {
                        a.onClick();
                        dismiss(t.id);
                      }}
                      className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-strong"
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
